import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/core-plugin-api';

export interface BugsyThread {
  thread_id: string;
  title: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * One piece of evidence behind an answer, as the service emits it
 * (`chat.py:1122`).
 *
 * There is deliberately no required `title`: the service does not send one
 * today. `title` arrives with the service-side change that adds it, so every
 * consumer must fall back to deriving a label from `source_uri`.
 */
export interface BugsyCitation {
  citation_id: string;
  source_uri: string;
  snapshot_id: string;
  excerpt: string;
  model_excerpt: string;
  title?: string;
}

export type BugsyVerification = 'verified' | 'unverified' | 'skipped';

export interface BugsyMessageMetadata {
  citations?: BugsyCitation[];
  /** The service persisted this answer after the reader disconnected. */
  truncated?: boolean;
  /**
   * The turn's telemetry, persisted next to the citations (`routes.py:491`).
   * It is mostly operator data; the page reads only the token counts, which
   * are what lets a replayed conversation show what it has cost so far
   * instead of starting from zero.
   */
  retrieval?: {
    input_tokens?: number;
    output_tokens?: number;
  };
  /** The agent run this assistant message belongs to (slash-command turns only). */
  run_id?: string;
  /**
   * Whether the post-answer source check ran (guardrails G3). `unverified`
   * means it fell back to a lexical check; the page shows a badge.
   */
  verification?: BugsyVerification;
}

export interface BugsyMessage {
  message_id: string;
  role: string;
  content: string;
  created_at?: string;
  metadata?: BugsyMessageMetadata;
}

/**
 * One frame of a streaming reply.
 *
 * A lifecycle envelope rather than bare tokens, following the same shape the
 * Anthropic and OpenAI streaming APIs use: progress and metadata travel
 * alongside the text so a client can render a wait it would otherwise have to
 * guess at.
 *
 * - `stage` marks the phases with no output of their own — retrieval, and the
 *   groundedness check after the last token.
 * - `token` carries incremental text.
 * - `revised` means verification disagreed with what was streamed; its `answer`
 *   supersedes the accumulated text.
 * - `error` is a failure after headers were already sent. Without it a truncated
 *   stream is indistinguishable from a finished one.
 * - `ping` is a keepalive and carries nothing.
 *
 * **Unknown types must be ignored.** New ones get added server-side, and a
 * client that throws on them turns an additive change into an outage.
 */

/** A slash command the reader may run (`GET /tools`, already filtered by the gateway). */
export interface BugsyCommand {
  name: string;
  description: string;
  usage: string;
  permission: string;
  allowed_groups: string[];
}

export interface BugsyApprovalField {
  name: string;
  label: string;
  value: string;
  multiline?: boolean;
}

/** What an approval card shows. Only `editable_fields` may be changed before approving. */
export interface BugsyApprovalPreview {
  title: string;
  fields: BugsyApprovalField[];
  notes?: string[];
  editable_fields: string[];
}

export type BugsyRunStatus =
  | 'running'
  | 'awaiting_approval'
  | 'awaiting_auth'
  | 'succeeded'
  | 'failed'
  | 'cancelled';

export interface BugsyToolCallView {
  tool_call_id: string;
  tool: string;
  version: string;
  status: string;
  summary: string;
  latency_ms?: number;
}

export interface BugsyApprovalView {
  approval_id: string;
  step: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  preview: BugsyApprovalPreview;
  expires_at: string;
  decided_by: string | null;
  edited_fields: Record<string, unknown>;
}

export interface BugsyArtifactView {
  artifact_id: string;
  kind: string;
  filename: string;
  size?: number;
  /** Presigned and short-lived. Absent when the service streams content itself. */
  url?: string | null;
  /** The live `artifact` event always carries one; the run view has `null` when the artifact stored none. */
  preview?: Record<string, unknown> | null;
}

/** What Save sends to arch-drawio (`POST /diagrams`, `POST /diagrams/:id/versions`). */
export interface BugsyDiagramVersion {
  xml: string;
  spec?: Record<string, unknown> | null;
  review_summary?: Record<string, unknown> | null;
}

/** A saved diagram and the version a Save produced. */
export interface BugsySavedDiagram {
  id: string;
  version: number;
}

/** `GET /threads/:id/runs/:runId`: everything needed to rebuild a run's cards after a reload. */
export interface BugsyRunView {
  run_id: string;
  thread_id: string;
  workflow: string;
  status: BugsyRunStatus;
  current_step: number;
  error: string | null;
  resumable: boolean;
  tool_calls: BugsyToolCallView[];
  approvals: BugsyApprovalView[];
  artifacts: BugsyArtifactView[];
}

export type BugsyStreamEvent =
  | { type: 'stage'; stage: 'retrieving' | 'generating' | 'verifying' }
  | { type: 'token'; delta: string }
  | { type: 'usage'; input_tokens: number; output_tokens: number }
  | { type: 'revised'; answer: string; reason: string }
  | { type: 'ping' }
  | {
      type: 'final';
      answer: string;
      citations?: unknown[];
      title?: string;
      run_id?: string | null;
      verification?: BugsyVerification;
    }
  | { type: 'stored'; message_id: string }
  | { type: 'error'; message: string }
  | { type: 'run_started'; run_id: string; workflow: string; step_budget: number }
  | { type: 'step_started'; run_id: string; step: string; label: string }
  | { type: 'step_finished'; run_id: string; step: string; label: string }
  | { type: 'tool_call_start'; tool_call_id: string; tool: string; version: string; risk_class: string }
  | { type: 'tool_call_args'; tool_call_id: string; summary: string }
  | { type: 'tool_call_result'; tool_call_id: string; status: string; summary: string; is_error: boolean }
  | { type: 'approval_required'; run_id: string; approval_id: string; preview: BugsyApprovalPreview; expires_at: string }
  | { type: 'auth_required'; run_id: string; provider: string; consent_url: string }
  | {
      type: 'artifact';
      run_id: string;
      artifact_id: string;
      kind: string;
      filename: string;
      url: string | null;
      preview: Record<string, unknown>;
    }
  | { type: 'run_finished'; run_id: string; status: BugsyRunStatus; message: string }
  // `resumable` is absent from a bugsy-api that predates it.
  | { type: 'run_error'; run_id: string; message: string; resumable?: boolean };

/** Human-readable label for each phase, for the streaming status line. */
export const BUGSY_STAGE_LABELS: Record<string, string> = {
  retrieving: 'Searching the knowledge base',
  generating: 'Drafting response',
  verifying: 'Checking sources',
};

/**
 * Why an answer was unhelpful — Bugsy's own taxonomy, which names the stage
 * that failed rather than asking the reader to write the diagnosis themselves.
 * `good` is the positive case.
 */
export type BugsyFeedbackBucket =
  | 'good'
  | 'bad_retrieval'
  | 'bad_grounding'
  | 'bad_abstention'
  | 'bad_latency'
  | 'bad_safety';

/** What each failure bucket is called for a reader who is not an operator. */
export const BUGSY_FEEDBACK_REASONS: { bucket: BugsyFeedbackBucket; label: string }[] = [
  { bucket: 'bad_retrieval', label: "Didn't find the right source" },
  { bucket: 'bad_grounding', label: "Answer doesn't match its sources" },
  { bucket: 'bad_abstention', label: 'Refused a question it should answer' },
  { bucket: 'bad_latency', label: 'Took too long' },
  { bucket: 'bad_safety', label: 'Inappropriate or unsafe' },
];

/**
 * The evidence behind one citation -- the source snapshot and the excerpt
 * actually retrieved for it, as opposed to `BugsyCitation.model_excerpt`
 * (what was handed to the model) which travels with the answer itself.
 */
export interface BugsyProvenance {
  citation_id: string;
  snapshot_id: string;
  chunk_id: string;
  excerpt: string;
  anchor?: string;
  metadata?: unknown;
}

/**
 * One file's outcome from `uploadAttachments`.
 *
 * A multi-file upload is not atomic -- the gateway sends each file to Bugsy
 * one at a time with no rollback -- so a batch can partially succeed. This is
 * therefore always a per-file result, never a single success-or-failure for
 * the whole call: a caller that collapses it into one verdict would tell a
 * reader who attached five files and had one fail that all five failed, or
 * that all five succeeded.
 */
export type BugsyUploadOutcome =
  | { ok: true; attachment_id: string; job_id: string; filename: string }
  | { ok: false; filename: string; message: string; status: number };

/** The ingest job an upload starts, as `GET /jobs/:jobId` reports it. */
export interface BugsyJob {
  job_id: string;
  job_type: string;
  status: string;
}

export interface BugsyApi {
  /** The reader's conversations; `archived` asks for the ones the rail hides. */
  listThreads(options?: { archived?: boolean }): Promise<BugsyThread[]>;
  createThread(title?: string): Promise<BugsyThread>;
  listMessages(threadId: string): Promise<BugsyMessage[]>;
  streamReply(
    threadId: string,
    content: string,
    signal?: AbortSignal,
  ): AsyncGenerator<BugsyStreamEvent>;
  submitFeedback(
    threadId: string,
    feedback: { bucket: BugsyFeedbackBucket; detail?: string; messageId?: string },
  ): Promise<void>;
  getProvenance(citationId: string): Promise<BugsyProvenance>;
  renameThread(threadId: string, title: string): Promise<void>;
  /** Soft-hides the thread: a row in `chat_thread_archives` that `list_threads` filters out. Nothing is deleted. */
  archiveThread(threadId: string): Promise<void>;
  /** Puts an archived thread back in the list. Idempotent on a thread that is not archived. */
  unarchiveThread(threadId: string): Promise<void>;
  /**
   * Uploads one or more files as attachments on a thread. See
   * `BugsyUploadOutcome`: the result is always one entry per file passed in,
   * in the same order, even when some of them failed.
   */
  uploadAttachments(threadId: string, files: File[]): Promise<BugsyUploadOutcome[]>;
  /** Polls the ingest job an upload started, so the reader who attached the file can watch it progress. */
  getJob(jobId: string): Promise<BugsyJob>;
  /** Starts a corpus sync for one source type. Gated server-side on `bugsy.sync`. */
  triggerSync(sourceType: string): Promise<{ job_id: string; source_type: string }>;
  /** Slash commands this reader may run. The gateway has already filtered them. */
  listCommands(): Promise<BugsyCommand[]>;
  getRun(threadId: string, runId: string): Promise<BugsyRunView>;
  /** Approving continues the run: the rest of it streams back like a reply. */
  decideApproval(
    threadId: string,
    approvalId: string,
    decision: 'approved' | 'rejected',
    editedFields: Record<string, unknown>,
    signal?: AbortSignal,
  ): AsyncGenerator<BugsyStreamEvent>;
  resumeRun(threadId: string, runId: string, signal?: AbortSignal): AsyncGenerator<BugsyStreamEvent>;
  getArtifact(artifactId: string): Promise<BugsyArtifactView>;
  /** Fetches a fresh URL every time: the one on the `artifact` event expires after 15 minutes. */
  downloadArtifact(artifact: BugsyArtifactView): Promise<void>;
  /** The artifact's bytes, always through the gateway (the diagram card reads the .drawio and the .svg). */
  fetchArtifactContent(artifactId: string): Promise<Blob>;
  /** Saves a Bugsy diagram as a new saved diagram in arch-drawio, owned by the signed-in user. */
  saveDiagram(title: string, version: BugsyDiagramVersion): Promise<BugsySavedDiagram>;
  /** Adds a version to a saved diagram the signed-in user owns (a `/diagram edit <id>` result). */
  addDiagramVersion(diagramId: string, version: BugsyDiagramVersion): Promise<BugsySavedDiagram>;
}

export const bugsyApiRef = createApiRef<BugsyApi>({
  id: 'plugin.bugsy.service',
});

export class BugsyBackendClient implements BugsyApi {
  constructor(
    private readonly deps: { discoveryApi: DiscoveryApi; fetchApi: FetchApi },
  ) {}

  private async baseUrl() {
    return this.deps.discoveryApi.getBaseUrl('bugsy');
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await this.deps.fetchApi.fetch(`${await this.baseUrl()}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...init?.headers },
    });
    if (!response.ok) {
      throw await describeFailure(response);
    }
    return (await response.json()) as T;
  }

  async listThreads(options?: { archived?: boolean }): Promise<BugsyThread[]> {
    return this.request(options?.archived ? '/threads?archived=true' : '/threads');
  }

  async createThread(title = 'New Chat'): Promise<BugsyThread> {
    return this.request('/threads', { method: 'POST', body: JSON.stringify({ title }) });
  }

  async listMessages(threadId: string): Promise<BugsyMessage[]> {
    return this.request(`/threads/${encodeURIComponent(threadId)}/messages`);
  }

  async getProvenance(citationId: string): Promise<BugsyProvenance> {
    return this.request(`/provenance/${encodeURIComponent(citationId)}`);
  }

  async renameThread(threadId: string, title: string): Promise<void> {
    await this.request(`/threads/${encodeURIComponent(threadId)}/rename`, {
      method: 'POST',
      body: JSON.stringify({ title }),
    });
  }

  async archiveThread(threadId: string): Promise<void> {
    await this.request(`/threads/${encodeURIComponent(threadId)}/archive`, { method: 'POST' });
  }

  async unarchiveThread(threadId: string): Promise<void> {
    await this.request(`/threads/${encodeURIComponent(threadId)}/unarchive`, { method: 'POST' });
  }

  /**
   * `request()` always sends `Content-Type: application/json`, which would
   * stomp the multipart boundary the browser needs to set itself for a
   * `FormData` body -- so this bypasses it and calls `fetchApi` directly.
   *
   * The gateway's per-file outcome array is the normal response, on both
   * success and failure statuses (even a 404 "thread not found" carries one).
   * A request that never reached per-file processing at all (a malformed
   * multipart body, too many parts) instead returns a bare `{ message }` --
   * that case is normalised here into the same per-file shape, applying the
   * one message to every file in the batch, so a caller only ever handles
   * one response shape.
   */
  async uploadAttachments(threadId: string, files: File[]): Promise<BugsyUploadOutcome[]> {
    const form = new FormData();
    for (const file of files) {
      form.append('file', file, file.name);
    }

    const response = await this.deps.fetchApi.fetch(
      `${await this.baseUrl()}/threads/${encodeURIComponent(threadId)}/uploads`,
      { method: 'POST', body: form },
    );

    const body = await response.json().catch(() => undefined);
    if (Array.isArray(body)) {
      return body as BugsyUploadOutcome[];
    }
    const message =
      (body as { message?: string } | undefined)?.message ??
      `Bugsy request failed (${response.status} ${response.statusText}).`;
    return files.map(file => ({ ok: false, filename: file.name, message, status: response.status }));
  }

  async getJob(jobId: string): Promise<BugsyJob> {
    return this.request(`/jobs/${encodeURIComponent(jobId)}`);
  }

  async triggerSync(sourceType: string): Promise<{ job_id: string; source_type: string }> {
    return this.request(`/sync/${encodeURIComponent(sourceType)}`, { method: 'POST' });
  }

  async submitFeedback(
    threadId: string,
    feedback: { bucket: BugsyFeedbackBucket; detail?: string; messageId?: string },
  ): Promise<void> {
    await this.request(`/threads/${encodeURIComponent(threadId)}/feedback`, {
      method: 'POST',
      body: JSON.stringify({
        bucket: feedback.bucket,
        detail: feedback.detail ?? '',
        message_id: feedback.messageId,
      }),
    });
  }

  async *streamReply(
    threadId: string,
    content: string,
    signal?: AbortSignal,
  ): AsyncGenerator<BugsyStreamEvent> {
    yield* this.stream(`/threads/${encodeURIComponent(threadId)}/reply`, { content }, signal);
  }

  async *decideApproval(
    threadId: string,
    approvalId: string,
    decision: 'approved' | 'rejected',
    editedFields: Record<string, unknown>,
    signal?: AbortSignal,
  ): AsyncGenerator<BugsyStreamEvent> {
    yield* this.stream(
      `/threads/${encodeURIComponent(threadId)}/approvals/${encodeURIComponent(approvalId)}`,
      { decision, edited_fields: editedFields },
      signal,
    );
  }

  async *resumeRun(threadId: string, runId: string, signal?: AbortSignal): AsyncGenerator<BugsyStreamEvent> {
    yield* this.stream(`/threads/${encodeURIComponent(threadId)}/runs/${encodeURIComponent(runId)}/resume`, undefined, signal);
  }

  async listCommands(): Promise<BugsyCommand[]> {
    return (await this.request<{ commands: BugsyCommand[] }>('/tools')).commands;
  }

  async getRun(threadId: string, runId: string): Promise<BugsyRunView> {
    return this.request(`/threads/${encodeURIComponent(threadId)}/runs/${encodeURIComponent(runId)}`);
  }

  async getArtifact(artifactId: string): Promise<BugsyArtifactView> {
    return this.request(`/artifacts/${encodeURIComponent(artifactId)}`);
  }

  async downloadArtifact(artifact: BugsyArtifactView): Promise<void> {
    // A presigned S3 URL is opened, not fetched: fetchApi would add the
    // Backstage Authorization header, which S3 rejects on a presigned request.
    // The window is opened now, synchronously inside the click, because a
    // popup opened after the await below is blocked by the browser; it is
    // pointed at the fresh URL once that arrives.
    const win = artifact.url ? window.open('', '_blank') : null;
    let fresh: BugsyArtifactView;
    try {
      fresh = await this.getArtifact(artifact.artifact_id);
    } catch (error) {
      win?.close();
      throw error;
    }
    if (fresh.url) {
      if (win) {
        win.opener = null; // what 'noopener' would have given
        win.location.href = fresh.url;
      } else {
        window.open(fresh.url, '_blank', 'noopener');
      }
      return;
    }
    win?.close();
    const response = await this.deps.fetchApi.fetch(
      `${await this.baseUrl()}/artifacts/${encodeURIComponent(artifact.artifact_id)}/content`,
    );
    if (!response.ok) {
      throw await describeFailure(response);
    }
    const href = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = href;
    link.download = fresh.filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoking in the same tick as the click can cancel the download.
    setTimeout(() => URL.revokeObjectURL(href), 0);
  }

  async fetchArtifactContent(artifactId: string): Promise<Blob> {
    const response = await this.deps.fetchApi.fetch(
      `${await this.baseUrl()}/artifacts/${encodeURIComponent(artifactId)}/content`,
    );
    if (!response.ok) {
      throw await describeFailure(response);
    }
    return response.blob();
  }

  async saveDiagram(title: string, version: BugsyDiagramVersion): Promise<BugsySavedDiagram> {
    return this.archDrawio('/diagrams', { title: title.slice(0, 200), ...version, source: 'bugsy' });
  }

  async addDiagramVersion(diagramId: string, version: BugsyDiagramVersion): Promise<BugsySavedDiagram> {
    return this.archDrawio(`/diagrams/${encodeURIComponent(diagramId)}/versions`, { ...version, source: 'bugsy' });
  }

  /** arch-drawio-backend directly, with the user's Backstage token: Save is the user's own write (P1-R11). */
  private async archDrawio<T>(path: string, body: unknown): Promise<T> {
    const response = await this.deps.fetchApi.fetch(`${await this.deps.discoveryApi.getBaseUrl('arch-drawio')}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      let errorMsg: string;
      try {
        const errorBody = await response.json();
        if (response.status === 409) {
          errorMsg = 'Someone saved this diagram at the same time. Save again to retry.';
        } else {
          errorMsg = typeof errorBody.error === 'string' ? errorBody.error : errorBody.error?.message || errorBody.message || response.statusText;
        }
      } catch {
        errorMsg = `Saving the diagram failed (${response.status} ${response.statusText}).`;
      }
      throw new Error(errorMsg);
    }
    return (await response.json()) as T;
  }

  /**
   * POSTs and reads the response as SSE frames. `fetch` rather than
   * `EventSource`, because every stream here is a POST that needs the
   * Backstage auth header -- `EventSource` can do neither.
   */
  private async *stream(path: string, body: unknown, signal?: AbortSignal): AsyncGenerator<BugsyStreamEvent> {
    const response = await this.deps.fetchApi.fetch(`${await this.baseUrl()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      throw await describeFailure(response);
    }
    if (!response.body) {
      throw new Error('Bugsy returned no response body to stream.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    // Frames are delimited by a blank line, and a chunk boundary can land
    // anywhere — including mid-frame. Anything after the last delimiter stays
    // buffered until the rest of it arrives.
    let buffer = '';

    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';

        for (const frame of frames) {
          const event = parseFrame(frame);
          if (event) {
            yield event;
          }
        }
      }

      const trailing = parseFrame(buffer);
      if (trailing) {
        yield trailing;
      }
    } finally {
      reader.cancel().catch(() => {});
    }
  }
}

function parseFrame(frame: string): BugsyStreamEvent | undefined {
  const data = frame
    .split('\n')
    .filter(line => line.startsWith('data:'))
    .map(line => line.slice('data:'.length).trim())
    .join('');

  // Comment-only frames (SSE keepalives) carry no data line.
  if (!data) {
    return undefined;
  }
  try {
    return JSON.parse(data) as BugsyStreamEvent;
  } catch {
    return undefined;
  }
}

/** A non-OK Bugsy response. `message` is what the chat view shows the reader. */
export class BugsyRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'BugsyRequestError';
  }
}

async function describeFailure(response: Response): Promise<BugsyRequestError> {
  let message: string | undefined;
  let retryAfter: number | undefined;
  try {
    const body = (await response.json()) as { message?: string; error?: { message?: string }; retry_after_seconds?: unknown };
    message = body.message ?? body.error?.message;
    if (typeof body.retry_after_seconds === 'number') {
      retryAfter = body.retry_after_seconds;
    }
  } catch {
    // fall through to the status text
  }
  return new BugsyRequestError(
    message ?? `Bugsy request failed (${response.status} ${response.statusText}).`,
    response.status,
    retryAfter,
  );
}
